<template>
  <div class="cms-page-form">
    <h1>Dodaj podstronę</h1>

    <el-form ref="formRef" :model="form" label-width="160px" class="cms-page-form__body">
      <el-form-item label="Tytuł" prop="title" :rules="[{ required: true, message: 'Tytuł jest wymagany' }]">
        <el-input v-model="form.title" placeholder="np. Mieszkania na sprzedaż w Warszawie" @input="onTitleInput" />
      </el-form-item>

      <el-form-item label="Slug (URL)" prop="slug" :rules="[{ required: true, message: 'Slug jest wymagany' }]">
        <el-input v-model="form.slug" placeholder="np. mieszkania-warszawa-tanio" @input="onSlugInput">
          <template slot="prepend">{{ frontendUrl }}/strony/</template>
        </el-input>
        <div class="slug-status" :class="slugStatusClass">
          <span v-if="slugCheckLoading">Sprawdzam dostępność...</span>
          <span v-else-if="slugCheck === null">Slug musi być w formacie kebab-case (małe litery, cyfry, myślniki).</span>
          <span v-else-if="slugCheck.available">✓ Slug jest dostępny.</span>
          <span v-else>✗ {{ slugCheck.message || slugErrorMessage(slugCheck.reason) }}</span>
        </div>
      </el-form-item>

      <el-form-item label="Opublikowana">
        <el-switch v-model="form.published" active-text="Opublikowana" inactive-text="Draft" />
      </el-form-item>

      <el-form-item label="Tryb edycji">
        <el-radio-group v-model="editMode" size="small">
          <el-radio-button label="wysiwyg">Edytor WYSIWYG</el-radio-button>
          <el-radio-button label="html">Raw HTML</el-radio-button>
        </el-radio-group>
        <el-button v-if="canRestoreHtml" size="mini" type="text" icon="el-icon-refresh-left"
                   class="restore-html-btn" @click="restoreHtml">
          Przywróć Raw HTML (Quill uprościł treść)
        </el-button>
        <div class="edit-mode-tip">
          <span v-if="editMode === 'wysiwyg'">Standardowy edytor — formatowanie, obrazki, listy. UWAGA: Quill uprości niestandardowe tagi (&lt;section&gt;, custom classes) — użyj &quot;Raw HTML&quot; dla landing page'ów.</span>
          <span v-else>Surowy HTML. Tailwind CSS działa (popularne klasy). Tagi &lt;script&gt;, &lt;style&gt; i event handlery (onclick itd.) są usuwane przy zapisie.</span>
        </div>
      </el-form-item>

      <el-form-item label="Treść" prop="content" :rules="[{ required: true, message: 'Treść jest wymagana' }]">
        <vue-editor v-if="editMode === 'wysiwyg'" v-model="form.content" :editorOptions="editorSettings" />
        <el-input v-else v-model="form.content" type="textarea" :rows="20"
                  placeholder="<section class=&quot;py-12 bg-pink-50&quot;>...</section>"
                  resize="vertical" class="html-editor-textarea" />
      </el-form-item>

      <el-divider content-position="left">SEO i Open Graph</el-divider>

      <el-form-item label="Meta title">
        <el-input v-model="form.meta_title" placeholder="Domyślnie = tytuł" maxlength="255" show-word-limit />
      </el-form-item>

      <el-form-item label="Meta description">
        <el-input v-model="form.meta_description" type="textarea" :rows="3" maxlength="500" show-word-limit
                  placeholder="Opis pokazywany w Google i na social media." />
      </el-form-item>

      <el-form-item label="Meta keywords">
        <el-input v-model="form.meta_keywords" placeholder="słowo1, słowo2, słowo3" maxlength="500" show-word-limit />
      </el-form-item>

      <el-form-item label="OG image">
        <el-upload :on-change="handleImageChange" :on-remove="handleImageRemove"
                   :auto-upload="false" :limit="1" :file-list="ogImageList" accept="image/*">
          <el-button size="small" type="primary">Wybierz obraz</el-button>
          <div slot="tip" class="el-upload__tip">JPG/PNG/WEBP, max 2MB. Zalecane 1200x630px.</div>
        </el-upload>
      </el-form-item>

      <el-form-item>
        <el-button type="primary" :loading="saving" :disabled="!canSubmit" @click="submit">Zapisz</el-button>
        <el-button @click="cancel">Anuluj</el-button>
      </el-form-item>
    </el-form>
  </div>
</template>

<script>
import { VueEditor, Quill } from 'vue2-editor';
import ImageResize from 'quill-image-resize-vue';
import { ImageDrop } from 'quill-image-drop-module';
import CmsPagesResource from '@/api/cmsPages';

Quill.register('modules/imageDrop', ImageDrop);
Quill.register('modules/imageResize', ImageResize);

const cmsPagesApi = new CmsPagesResource();

export default {
  name: 'CmsPagesAdd',
  components: { VueEditor },
  data() {
    return {
      form: {
        title: '',
        slug: '',
        content: '',
        meta_title: '',
        meta_description: '',
        meta_keywords: '',
        published: true,
      },
      ogImageFile: null,
      ogImageList: [],
      editMode: 'wysiwyg',
      htmlBackup: '',
      slugManuallyEdited: false,
      slugCheck: null,
      slugCheckLoading: false,
      slugCheckTimer: null,
      saving: false,
      editorSettings: {
        modules: {
          imageDrop: true,
          imageResize: {},
          toolbar: {
            container: [
              ['bold', 'italic', 'underline', 'strike'],
              [{ header: [false, 1, 2, 3, 4, 5, 6] }],
              [{ align: '' }, { align: 'center' }, { align: 'right' }, { align: 'justify' }],
              ['blockquote', 'code-block'],
              [{ list: 'ordered' }, { list: 'bullet' }],
              [{ indent: '-1' }, { indent: '+1' }],
              [{ color: [] }, { background: [] }],
              ['link', 'image'],
              ['clean'],
            ],
          },
        },
      },
    };
  },
  computed: {
    frontendUrl() {
      return (process.env.VUE_APP_FRONTEND_URL || 'https://dazu.pl').replace(/\/$/, '');
    },
    slugStatusClass() {
      if (this.slugCheckLoading) return 'is-checking';
      if (!this.slugCheck) return 'is-neutral';
      return this.slugCheck.available ? 'is-ok' : 'is-error';
    },
    canRestoreHtml() {
      // Pokazuj przycisk restore tylko gdy w WYSIWYG, backup istnieje i różni się od bieżącej treści
      return this.editMode === 'wysiwyg'
        && this.htmlBackup
        && this.htmlBackup !== this.form.content;
    },
    canSubmit() {
      return this.form.title && this.form.slug && this.form.content
        && this.slugCheck && this.slugCheck.available
        && !this.saving;
    },
  },
  watch: {
    // Zapisz raw HTML do backupu zanim Quill go uprości po przełączeniu na WYSIWYG.
    // Quill v-model parse'uje content przez swój DOM i wycina nieobsługiwane tagi
    // (<section>, custom classes itd.) — admin może stracić swój layout. Backup pozwala
    // wrócić do oryginału jednym klikiem.
    editMode(newMode, oldMode) {
      if (oldMode === 'html' && newMode === 'wysiwyg' && this.form.content) {
        this.htmlBackup = this.form.content;
      }
    },
  },
  methods: {
    restoreHtml() {
      this.form.content = this.htmlBackup;
      this.editMode = 'html';
    },
    slugify(text) {
      return String(text || '')
        .toLowerCase()
        .replace(/ą/g, 'a').replace(/ć/g, 'c').replace(/ę/g, 'e')
        .replace(/ł/g, 'l').replace(/ń/g, 'n').replace(/ó/g, 'o')
        .replace(/ś/g, 's').replace(/ź/g, 'z').replace(/ż/g, 'z')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    },
    onTitleInput() {
      if (!this.slugManuallyEdited) {
        this.form.slug = this.slugify(this.form.title);
        this.scheduleSlugCheck();
      }
    },
    onSlugInput() {
      this.slugManuallyEdited = true;
      this.scheduleSlugCheck();
    },
    scheduleSlugCheck() {
      if (this.slugCheckTimer) clearTimeout(this.slugCheckTimer);
      this.slugCheck = null;
      const slug = this.form.slug;
      if (!slug) return;
      this.slugCheckLoading = true;
      this.slugCheckTimer = setTimeout(async() => {
        try {
          // checkSlug zwraca plain { available, reason, message } (przez response()->success([...]))
          const res = await cmsPagesApi.checkSlug(slug);
          this.slugCheck = res || { available: false };
        } catch (e) {
          this.slugCheck = { available: false, message: 'Błąd sprawdzania slug-a.' };
        } finally {
          this.slugCheckLoading = false;
        }
      }, 300);
    },
    slugErrorMessage(reason) {
      const messages = {
        format: 'Nieprawidłowy format (tylko małe litery, cyfry, myślniki).',
        reserved: 'Ten slug jest zarezerwowany przez system.',
        taken: 'Ten slug jest już używany.',
        empty: 'Slug nie może być pusty.',
      };
      return messages[reason] || 'Slug niedostępny.';
    },
    handleImageChange(file) {
      this.ogImageFile = file.raw;
      this.ogImageList = [file];
    },
    handleImageRemove() {
      this.ogImageFile = null;
      this.ogImageList = [];
    },
    async submit() {
      this.$refs.formRef.validate(async(valid) => {
        if (!valid || !this.canSubmit) return;
        this.saving = true;
        try {
          const formData = new FormData();
          formData.append('title', this.form.title);
          formData.append('slug', this.form.slug);
          formData.append('content', this.form.content);
          if (this.form.meta_title) formData.append('meta_title', this.form.meta_title);
          if (this.form.meta_description) formData.append('meta_description', this.form.meta_description);
          if (this.form.meta_keywords) formData.append('meta_keywords', this.form.meta_keywords);
          formData.append('published', this.form.published ? '1' : '0');
          if (this.ogImageFile) formData.append('og_image', this.ogImageFile);

          const res = await cmsPagesApi.store(formData);
          // Resource auto-wrap → { data: { id, slug, ... } }
          const page = (res && res.data) ? res.data : res;

          this.$message.success('Podstrona zapisana.');
          if (page && page.id) {
            this.$router.push({ path: '/cms-pages/edit/' + page.id });
          } else {
            this.$router.push({ path: '/cms-pages/list' });
          }
        } catch (err) {
          let msg = 'Nie udało się zapisać podstrony.';
          if (err && err.response && err.response.data && err.response.data.errors) {
            const errors = err.response.data.errors;
            const flat = [];
            Object.keys(errors).forEach(k => {
              const arr = errors[k];
              if (Array.isArray(arr)) flat.push.apply(flat, arr);
              else flat.push(arr);
            });
            msg = flat.join(' ');
          }
          this.$message.error(msg);
        } finally {
          this.saving = false;
        }
      });
    },
    cancel() {
      this.$router.push({ path: '/cms-pages/list' });
    },
  },
};
</script>

<style lang="scss" scoped>
.cms-page-form {
  padding: 20px;
  &__body {
    max-width: 960px;
  }
  .slug-status {
    margin-top: 6px;
    font-size: 12px;
    &.is-ok { color: #67c23a; }
    &.is-error { color: #f56c6c; }
    &.is-checking { color: #909399; }
    &.is-neutral { color: #909399; }
  }
  .edit-mode-tip {
    margin-top: 6px;
    font-size: 12px;
    color: #909399;
  }
  .restore-html-btn {
    margin-left: 12px;
    color: #e6a23c;
    &:hover { color: #c87f10; }
  }
  .html-editor-textarea ::v-deep textarea {
    font-family: 'Fira Code', 'Consolas', 'Courier New', monospace;
    font-size: 13px;
    line-height: 1.5;
  }
}
</style>
